import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scale, ShieldAlert, CheckCircle2, Loader2 } from "lucide-react";

const ACT_LABEL = {
  DBA: "Davis-Bacon Act (construction)",
  SCA: "Service Contract Act (services)",
};

// Dir 198 — a labor-bearing bid is NEVER priced without the governing wage act (DBA/SCA) confirmed.
// When intake could not authoritatively determine it, this prompts the officer BEFORE pricing proceeds.
export function WageActConfirm({ dealId, isOfficer, reload }) {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/bids/${dealId}/wage-act`);
      setData(r.data);
      const wa = r.data.wage_act || {};
      setChoice(["DBA", "SCA"].includes(wa.act) ? wa.act : "");
    } catch (e) { setData(null); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load wage-act state when the deal id changes
  useEffect(() => { load(); }, [dealId]);

  if (!data) return null;
  const wa = data.wage_act || {};
  if (!wa || !wa.labor_bearing) return null; // supply/commercial — no wage act applies

  const needsConfirm = data.needs_confirmation || (data.pricing_hold && !data.resolved_act);

  // Already resolved (authoritative or confirmed) — show a compact chip.
  if (data.resolved_act && !needsConfirm) {
    const confirmed = wa.officer_confirmed;
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="wage-act-chip" title={wa.basis}>
        <Scale className="h-3 w-3" />
        <span className="font-semibold">Wage act: {data.resolved_act}</span>
        <span className="opacity-70">· {confirmed ? "officer-confirmed" : "auto"}</span>
        <CheckCircle2 className="h-3 w-3" />
      </div>
    );
  }

  if (!needsConfirm) return null;

  const confirm = async () => {
    if (!["DBA", "SCA"].includes(choice)) { toast.error("Select DBA or SCA."); return; }
    setBusy(true);
    try {
      await api.post(`/bids/${dealId}/wage-act/confirm`, { act: choice });
      toast.success(`Wage act confirmed: ${choice}`);
      await load();
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="wage-act-confirm">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900">Confirm the governing wage act before pricing</div>
          <p className="text-[11px] text-amber-800 mt-1 max-w-2xl" data-testid="wage-act-basis">
            {wa.basis || "The governing wage act could not be determined from the solicitation."} Pricing is held
            until the wage act is confirmed.
          </p>
        </div>
      </div>

      {isOfficer ? (
        <div className="mt-3 space-y-2">
          {["DBA", "SCA"].map((a) => (
            <label
              key={a}
              className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer border ${choice === a ? "border-gold-dark bg-gold/5" : "border-zinc-200 hover:bg-white"}`}
              data-testid={`wage-act-opt-${a}`}>
              <input type="radio" name="wage-act" checked={choice === a} onChange={() => setChoice(a)} className="mt-0.5" />
              <span>
                <span className="text-xs font-medium text-zinc-900">{a}</span>
                <span className="block text-[10px] text-zinc-500">{ACT_LABEL[a]}</span>
              </span>
            </label>
          ))}
          <button
            onClick={confirm}
            disabled={busy || !choice}
            className="flex items-center gap-1.5 text-[11px] bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 disabled:opacity-50"
            data-testid="wage-act-confirm-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Scale className="h-3 w-3" />}
            Confirm wage act &amp; release pricing
          </button>
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-amber-700">An officer must confirm the wage act before pricing can proceed.</p>
      )}
    </div>
  );
}
